import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { BuildingStorefrontIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import type { ClaimableCamp } from '../data/campClaimSeedData';
import { determineClaimButtonAction, type ClaimButtonAction, hasClaimedCamp } from '../lib/authHelpers';
import { track } from '../lib/track';
import { useUserTypes } from '../hooks/useUserTypes';
import AlreadyClaimedModal from './AlreadyClaimedModal';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface ClaimCampCardProps {
  camp: ClaimableCamp;
  currentUserId?: string;
  onEditClick?: () => void;
}

/**
 * ClaimCampCard Component
 * 
 * Displays a card that allows camp owners to claim their listing.
 * Shows conditionally based on:
 * - Camp is unclaimed, OR
 * - Current user has not claimed this camp
 * 
 * Following CampWhiz design patterns:
 * - Uses btn-secondary for secondary actions
 * - Matches the existing card style (bg-white rounded-2xl shadow-lg p-6)
 * - Positioned below "Add to Your Calendar" card in the sidebar
 */
export default function ClaimCampCard({ camp, currentUserId, onEditClick }: ClaimCampCardProps) {
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const { isParent, isCampOwner } = useUserTypes();
  const [isLoading, setIsLoading] = useState(false);
  const [claimAction, setClaimAction] = useState<ClaimButtonAction | null>(null);
  const [showAlreadyClaimedModal, setShowAlreadyClaimedModal] = useState(false);
  const [campPlanId, setCampPlanId] = useState<string | null>(null);

  // Determine the appropriate action for this camp and user
  useEffect(() => {
    const determineAction = async () => {
      const action = await determineClaimButtonAction(session, camp.id);
      setClaimAction(action);
      
      // Also fetch camp plan_id to show upsell if needed
      if (action.action === 'show-dashboard') {
        try {
          const { data } = await supabase
            .from('camps')
            .select('plan_id')
            .eq('id', camp.id)
            .single();
          
          if (data) {
            setCampPlanId(data.plan_id);
          }
        } catch (error) {
          console.error('Error fetching camp plan:', error);
        }
      }
    };
    
    determineAction();
  }, [session, camp.id, currentUserId]);

  // Show loading state while determining action
  if (!claimAction) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8" role="region" aria-label="Claim camp listing">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // If action is to hide the card, return null
  if (claimAction.action === 'hide') {
    return null;
  }

  // Hide card for parent-only users when camp is unclaimed (they can't claim)
  // But show it if camp is claimed (to show claimed message) or if they're a camp owner
  if (session && isParent && !isCampOwner) {
    // Only hide if camp is not claimed and action is not to show claimed message
    if (claimAction.action !== 'show-claimed-message' && claimAction.action !== 'show-dashboard') {
      return null;
    }
  }

  // If action is to show dashboard, render the owner dashboard card
  if (claimAction.action === 'show-dashboard') {
    const hasPlan = campPlanId && ['trailhead', 'campfire', 'summit', 'everest'].includes(campPlanId);
    
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8" role="region" aria-label="Camp owner dashboard">
        <div className="text-center mb-4">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-2" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-gray-900">Your Camp</h2>
          <p className="text-sm text-gray-600 mt-1">
            You own this camp listing
          </p>
        </div>

        <div className="space-y-3">
          {hasPlan ? (
            <button
              onClick={onEditClick}
              className="btn btn-primary w-full"
            >
              Edit My Camp
            </button>
          ) : (
            <>
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200 mb-3">
                <p className="text-sm text-gray-800 font-medium mb-2">
                  🎉 Camp Claimed Successfully!
                </p>
                <p className="text-xs text-gray-600 mb-3">
                  To edit your camp details, photos, and connect with families, upgrade to a plan.
                </p>
                <Link
                  to="/pricing/owners"
                  className="btn btn-primary w-full text-sm"
                >
                  View Pricing Plans
                </Link>
              </div>
              <button
                onClick={onEditClick}
                className="btn btn-secondary w-full opacity-50 cursor-not-allowed"
                disabled
                title="Upgrade to a plan to edit your camp"
              >
                Edit My Camp (Upgrade Required)
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // If action is to show claimed message, render the claimed status
  if (claimAction.action === 'show-claimed-message') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-md p-4 max-w-lg" role="status" aria-label="Camp ownership status">
        <div className="flex items-center space-x-3">
          <img 
            src="/assets/campwhiz-favicon2.svg" 
            alt="CampWhiz" 
            className="h-8 w-8 flex-shrink-0"
          />
          <p className="text-sm text-gray-600">
            {claimAction.message || "This camp has been claimed by its owner."}
          </p>
        </div>
      </div>
    );
  }

  const handleClaimClick = async () => {
    setIsLoading(true);
    track('claim_started', { camp_id: camp.id, persona: 'camp_owner' });

    console.log('ClaimCampCard: handleClaimClick called', {
      campId: camp.id,
      campSlug: camp.slug,
      claimAction: claimAction?.action,
      redirectUrl: claimAction?.redirectUrl,
      hasSession: !!session
    });

    // Check if user already has a claimed camp
    if (session?.user?.id) {
      const alreadyHasCamp = await hasClaimedCamp(session.user.id);
      if (alreadyHasCamp) {
        setIsLoading(false);
        setShowAlreadyClaimedModal(true);
        return;
      }
    }

    // If user is logged in, claim the camp immediately and redirect to mycamp-teaser
    if (session?.user?.id) {
      try {
        // Claim the camp immediately (free claim)
        const { error: claimError } = await supabase
          .from('camps')
          .update({
            claimed: true,
            owner_id: session.user.id,
            claimed_at: new Date().toISOString()
            // Note: plan_id is set later when they pay for editing access
          })
          .eq('id', camp.id);
        
        if (claimError) {
          console.error('Error claiming camp:', claimError);
          toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
          setIsLoading(false);
          return;
        }
        
        console.log('Camp claimed successfully (free claim)');
        
        // Invalidate React Query cache to refresh camp data so other users see it as claimed
        queryClient.invalidateQueries({ queryKey: ['camp'] });
        queryClient.invalidateQueries({ queryKey: ['camps'] });
        
        // Store camp context and redirect to mycamp-teaser
        sessionStorage.setItem('pending_claim_camp_slug', camp.slug);
        sessionStorage.setItem('pending_claim_camp_id', camp.id);
        navigate('/mycamp-teaser');
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('Error claiming camp:', error);
        toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
        setIsLoading(false);
        return;
      }
    }

    // If user is not logged in, store camp context and redirect to signup/login
    // Store camp context for after registration
    sessionStorage.setItem('pending_claim_camp_slug', camp.slug);
    sessionStorage.setItem('pending_claim_camp_id', camp.id);
    console.log('ClaimCampCard: Stored camp context for signup:', {
      campSlug: camp.slug,
      campId: camp.id,
      sessionStorageAfter: {
        pending_claim_camp_slug: sessionStorage.getItem('pending_claim_camp_slug'),
        pending_claim_camp_id: sessionStorage.getItem('pending_claim_camp_id')
      }
    });
    
    // Redirect to signup/login with redirect to mycamp-teaser
    const redirectUrl = '/login?type=business&redirect=/mycamp-teaser';
    console.log('ClaimCampCard: Redirecting to signup/login:', redirectUrl);
    navigate(redirectUrl);
    setIsLoading(false);
  };

          return (
            <div className="bg-white rounded-2xl shadow-lg p-8" role="region" aria-label="Claim camp listing">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Is This Your Camp?
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Keep your camp details accurate and connect with local families.
                </p>
              </div>

      <button
        onClick={handleClaimClick}
        disabled={isLoading}
        className="btn btn-secondary w-full"
        aria-busy={isLoading}
        aria-label={isLoading ? 'Loading, please wait' : 'Claim your camp listing'}
      >
        {isLoading ? 'Loading...' : 'Claim Your Listing'}
      </button>

      <div className="mt-4 bg-yellow-50 rounded-xl p-4 border border-yellow-200" role="complementary" aria-label="Listing benefits">
        <div className="space-y-2 text-xs text-gray-800">
          <p className="font-medium">✓ Update your camp info anytime</p>
          <p className="font-medium">✓ Get discovered by nearby families</p>
          <p className="text-gray-700">
            See all features on <Link 
              to="/pricing/owners" 
              className="text-blue-600 hover:text-blue-800 underline transition-colors"
            >
              pricing page
            </Link>
          </p>
        </div>
      </div>

      <p className="mt-4 text-xs text-gray-600">
        🎉 <strong>Limited Time:</strong> Save 50% with Early Bird pricing
      </p>
      
      <AlreadyClaimedModal 
        isOpen={showAlreadyClaimedModal}
        onClose={() => setShowAlreadyClaimedModal(false)}
      />
    </div>
  );
}

